import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Box, Typography, IconButton, Icon } from '@mui/material'

// Local Import
import AdxTabsScrollable from 'components/reusable/tab/AdxTabsScrollable'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import createLeaveTabsWithout from '../../components/createLeaveTabsWithout'
import { formikInitialValue } from '../../resource/policyObjects'

// API services
import { usePostNewLeavePolicy, usePutLeavePolicy } from 'services/api/modules/leave/leavePolicy/'
import AdxButton from 'components/custom/AdxButton'

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required').max(50, 'Maximum 50 characters only'),
  description: Yup.string().optional().max(120, 'Maximum 120 characters only'),
  attachment_required: Yup.boolean(),
  reason_required: Yup.boolean(),
  half_day_option: Yup.boolean(),
  include_non_working_days: Yup.boolean(),
  eligible_amount: Yup.number()
    .required('Required')
    .min(0, (min) => `Value must be greater than ${min.min} `),
  eligible_period: Yup.string().when('eligible_amount', {
    is: (val) => val > 0,
    then: (schema) => schema.required('Select a period'),
    otherwise: (schema) => schema.optional(),
  }),
  category: Yup.object().shape({
    gender: Yup.array()
      .of(
        Yup.object().shape({
          data: Yup.number(),
          status: Yup.number().oneOf([1, 2], 'Value outside range'),
        })
      )
      .compact((v) => v.status === 2 || v.status === 0)
      .min(1, 'At least one value must be selected'),
    marriage_status: Yup.array()
      .of(
        Yup.object().shape({
          data: Yup.number(),
          status: Yup.number().oneOf([1, 2], 'Value outside range'),
        })
      )
      .compact((v) => v.status === 2 || v.status === 0)
      .min(1, 'At least one value must be selected'),
    employment_type: Yup.array()
      .of(
        Yup.object().shape({
          data: Yup.number(),
          status: Yup.number().oneOf([1, 2], 'Value outside range'),
        })
      )
      .compact((v) => v.status === 2 || v.status === 0)
      .min(1, 'At least one value must be selected'),
  }),
  approval_route_id: Yup.number().required('Select one of the approval layer'),
  restriction_amount: Yup.number()
    .nullable()
    .when('checkboxes.restriction', {
      is: (val) => val === true,
      then: (schema) =>
        schema
          .required('Required')
          .moreThan(0, ({ more }) => `The restriction must be greater than ${more}`),
      otherwise: (schema) => schema.optional(),
    }),
  day_prior: Yup.number()
    .nullable()
    .when('checkboxes.minDate', {
      is: (val) => val === true,
      then: (schema) =>
        schema
          .required('Required')
          .moreThan(0, ({ more }) => `The restriction must be greater than ${more}`),
      otherwise: (schema) => schema.optional(),
    }),
})

function CreateLeavePolicyWithoutEntitlement({ editMode, policy }) {
  const navigate = useNavigate()
  const mutation = usePostNewLeavePolicy()
  const mutationEdit = usePutLeavePolicy()

  const editModeInitialValues = () => ({
    name: policy.name || '',
    description: policy.description || '',
    attachment_required: policy.attachment_required === 1 ? true : false,
    reason_required: policy.reason_required === 1 ? true : false,
    half_day_option: policy.half_day_option === 1 ? true : false,
    include_non_working_days: policy.include_non_working_days === 1 ? true : false,
    eligible_amount: policy.eligible_amount || 0,
    eligible_period: policy.eligible_period || '',
    category: {
      gender: policy.category?.gender?.length
        ? policy.category.gender
        : [...Object.values(formikInitialValue.Gender)],
      marriage_status: policy.category?.marriage_status?.length
        ? [...policy.category.marriage_status.filter((item) => item.data !== 3)]
        : [...Object.values(formikInitialValue['Marriage Status'])],
      employment_type: policy.category?.employment_type?.length
        ? policy.category.employment_type
        : [...Object.values(formikInitialValue['Employment Type'])],
    },
    approval_route_id: policy.approval_route_id || '',
    restriction_amount: policy.restriction_amount || 0,
    day_prior: policy.day_prior || 0,
    checkboxes: {
      restriction: policy.restriction_amount ? true : false,
      minDate: policy.day_prior ? true : false,
    },
  })

  return (
    <Box width="100%" minHeight="100vh" bgcolor="gray-background.main">
      <Box width={{ xs: '100%', lg: '1000px' }} bgcolor="white.main" m="0 auto">
        <Formik
          initialValues={
            editMode && policy.id
              ? editModeInitialValues()
              : {
                  name: '',
                  description: '',
                  attachment_required: false,
                  reason_required: false,
                  half_day_option: false,
                  include_non_working_days: false,
                  eligible_amount: 0,
                  eligible_period: '',
                  category: {
                    gender: [...Object.values(formikInitialValue.Gender)],
                    marriage_status: [...Object.values(formikInitialValue['Marriage Status'])],
                    employment_type: [...Object.values(formikInitialValue['Employment Type'])],
                  },
                  approval_route_id: '',
                  restriction_amount: 0,
                  day_prior: 0,
                  checkboxes: {
                    restriction: false,
                    minDate: false,
                  },
                }
          }
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={true}
          onSubmit={async (values) => {
            let { checkboxes, ...data } = values

            data = {
              ...data,
              category: editMode
                ? data.category
                : {
                    ...data.category,
                    marriage_status: [
                      ...data.category.marriage_status,
                      {
                        data: 3,
                        status: data.category.marriage_status.find((el) => el.data === 2).status,
                      },
                    ],
                  },
              type: 2,
              cycle_period: 'yearly',
              accrual_type: 1,
              attachment_required: data.attachment_required ? 1 : 0,
              reason_required: data.reason_required ? 1 : 0,
              half_day_option: data.half_day_option ? 1 : 0,
              include_non_working_days: data.include_non_working_days ? 1 : 0,
              eligible_amount: data.eligible_amount ? data.eligible_amount : null,
              entitlement: [],
              eligible_period: data.eligible_period ? data.eligible_period : null,
              restriction_amount: checkboxes.restriction ? data.restriction_amount : null,
              day_prior: checkboxes.minDate ? data.day_prior : null,
              credit_deduction: 0,
              ...(policy?.status === 3 && editMode && { status: 1 }),
            }
            if (editMode) {
              mutationEdit.mutate(
                { id: policy.id, data, type: policy.type },
                {
                  onSuccess: () => navigate(-1),
                }
              )
            } else {
              mutation.mutate(data, {
                onSuccess: () => navigate(-1),
              })
            }
          }}
        >
          {(formikBag) => (
            <Form>
              <Box display="flex" alignItems="center" justifyContent="space-between" p={2}>
                <Typography
                  variant="h3"
                  color="dark-text.main"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  {editMode
                    ? `Edit Leave Policy ${policy.id}`
                    : `Create new leave policy - without entitlement`}
                </Typography>
                <IconButton onClick={() => navigate(-1)}>
                  <Icon>close</Icon>
                </IconButton>
              </Box>
              <AdxTabsScrollable
                sxPanel={{
                  height: 'calc(100vh - 218px)',
                }}
                panelGap={32}
              >
                {createLeaveTabsWithout.map((tab, index, arr) => (
                  <AdxTabsPanel
                    key={tab.number}
                    label={tab.label}
                    sx={{ p: 2, borderTop: index > 0 ? 1 : 0, borderColor: 'gray-line.main' }}
                  >
                    <Box display="flex" flexDirection="column" rowGap={1} py={2} width={1}>
                      <Typography variant="h4" color="dark-text.main" sx={{ fontWeight: 'bold' }}>
                        {tab.title}
                      </Typography>
                      <Typography variant="body1" color="light-gray-text.main">
                        {tab.subtitle}
                      </Typography>
                      {tab.forms &&
                        tab.forms({
                          formikBag,
                          editMode,
                          type: 'WITHOUT',
                          policy,
                        })}
                      {arr.length - 1 === index && (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="flex-end"
                          justifyContent="center"
                          gap={2}
                        >
                          {Object.keys(formikBag.errors).length > 0 &&
                            formikBag.submitCount > 0 && (
                              <Typography color="error.main" variant="subtitle2">
                                Please resolve all the errors from this page
                              </Typography>
                            )}
                        </Box>
                      )}
                    </Box>
                  </AdxTabsPanel>
                ))}
              </AdxTabsScrollable>
              <Box
                display="flex"
                gap={1}
                width={1}
                alignItems={'center'}
                justifyContent={'end'}
                py={3}
                px={2}
              >
                <AdxButton variant={'ghost'} onClick={() => navigate(-1)} sx={{ width: '200px' }}>
                  Cancel
                </AdxButton>
                <AdxButton
                  variant={'contained'}
                  onClick={() => formikBag.submitForm()}
                  isLoading={mutation.isLoading || mutationEdit.isLoading}
                  disabled={mutation.isLoading || mutationEdit.isLoading}
                  sx={{ width: '300px' }}
                >
                  {editMode ? `Save Policy` : `Create policy`}
                </AdxButton>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  )
}

export default CreateLeavePolicyWithoutEntitlement
